import { useEffect, useState, useCallback, useMemo } from "react";
import { DataTable } from "..";
import { getAllSeasons, deleteSeason } from "../../api/seasons";
import { ISeason } from "../../models/ISeason";
import { Plus } from "@mynaui/icons-react";
import moment from "moment";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { useDebouncedSearch } from "../../hooks";
import SeasonDialog from "../SeasonDialog";
import DeleteSeasonDialog from "../DeleteSeasonDialog";
import { Button, Card, Loader, Select, Tag } from "../../ui";

type Props = {
  canManage: boolean;
};

const SeasonsManagement = ({ canManage }: Props) => {
  const dispatch = useAppDispatch();
  const [seasons, setSeasons] = useState<ISeason[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSeasonDialogOpen, setIsSeasonDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState<ISeason | undefined>();
  const [seasonToDelete, setSeasonToDelete] = useState<ISeason | undefined>();

  const [search, setSearch] = useState("");
  const [sport, setSport] = useState(""); // "" = all sports
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string>("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const { searchValue, setSearchValue } = useDebouncedSearch(handleSearch, 500, "");

  const fetchSeasons = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getAllSeasons();
      setSeasons(data);
    } catch (err: any) {
      if (err?.message === "No seasons found") setSeasons([]);
      else dispatch(setGeneralAlertMessage({ message: err?.message || "Failed to load seasons", type: "error" }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchSeasons();
  }, [fetchSeasons]);

  const handleCreate = () => {
    setSelectedSeason(undefined);
    setIsSeasonDialogOpen(true);
  };

  const handleEdit = (season: ISeason) => {
    setSelectedSeason(season);
    setIsSeasonDialogOpen(true);
  };

  const handleDelete = (season: ISeason) => {
    setSeasonToDelete(season);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!seasonToDelete) return;
    try {
      await deleteSeason(seasonToDelete.id);
      dispatch(setGeneralAlertMessage({ message: "Season deleted successfully", type: "success" }));
      setSeasonToDelete(undefined);
      await fetchSeasons();
    } catch (error: any) {
      dispatch(setGeneralAlertMessage({ message: error?.message || "Failed to delete season", type: "error" }));
      throw error;
    }
  };

  const handleDialogClose = async (update?: boolean) => {
    setIsSeasonDialogOpen(false);
    setSelectedSeason(undefined);
    if (update) await fetchSeasons();
  };

  // The sports that have seasons, for the filter
  const sports = useMemo(
    () => Array.from(new Set(seasons.map((season) => season.typeName).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [seasons]
  );

  const filteredSeasons = useMemo(() => {
    let filtered = seasons.filter((season) => {
      if (sport && season.typeName !== sport) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return season.name.toLowerCase().includes(s) || season.typeName.toLowerCase().includes(s);
    });

    filtered = [...filtered].sort((a, b) => {
      let aVal: any = a[sortColumn as keyof ISeason];
      let bVal: any = b[sortColumn as keyof ISeason];

      if (sortColumn === "name") { aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase(); }
      else if (sortColumn === "typeName") { aVal = a.typeName.toLowerCase(); bVal = b.typeName.toLowerCase(); }
      else if (sortColumn === "createdAt") { aVal = new Date(a.createdAt).getTime(); bVal = new Date(b.createdAt).getTime(); }
      else if (sortColumn === "isActive") { aVal = a.isActive ? 1 : 0; bVal = b.isActive ? 1 : 0; }
      else if (sortColumn === "startDate") {
        aVal = a.startDate ? new Date(a.startDate).getTime() : 0;
        bVal = b.startDate ? new Date(b.startDate).getTime() : 0;
      }

      return sortDirection === "asc"
        ? (aVal > bVal ? 1 : aVal < bVal ? -1 : 0)
        : (aVal < bVal ? 1 : aVal > bVal ? -1 : 0);
    });

    return filtered;
  }, [seasons, sport, search, sortColumn, sortDirection]);

  const pageSize = 10;
  const totalPages = Math.ceil(filteredSeasons.length / pageSize);
  const paginated = filteredSeasons.slice((page - 1) * pageSize, page * pageSize);

  return (
    <Card
      className="seasons-management"
      title="Seasons"
      subtitle={`${filteredSeasons.length} ${filteredSeasons.length === 1 ? "season" : "seasons"}`}
      actions={
        <>
          <Select
            size="sm"
            className="seasons-sport-filter"
            aria-label="Filter seasons by sport"
            value={sport}
            onChange={(e) => {
              setSport(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All sports</option>
            {sports.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Select>
          {canManage && (
            <Button icon={<Plus size={16} />} onClick={handleCreate}>
              Add Season
            </Button>
          )}
        </>
      }
    >
      {loading && seasons.length === 0 ? (
        <Loader />
      ) : (
        <DataTable
          id="seasons-table"
          data={paginated}
          editable
          label="seasons"
          pageNumber={page}
          pageSize={pageSize}
          totalPages={totalPages}
          totalItems={filteredSeasons.length}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onViewItem={handleEdit}
          onPageChange={setPage}
          onSortChange={(col, dir) => { setSortColumn(col); setSortDirection(dir); setPage(1); }}
          canDelete={canManage}
          onDelete={handleDelete}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          loading={loading}
          columns={[
            { key: "name", label: "Name", sortable: true },
            { key: "typeName", label: "Tournament Type", sortable: true },
            {
              key: "startDate", label: "Start Date", sortable: true,
              render: (_: any, row: ISeason) => row.startDate ? moment(row.startDate).format("DD MMM, YYYY") : "-",
            },
            {
              key: "endDate", label: "End Date",
              render: (_: any, row: ISeason) => row.endDate ? moment(row.endDate).format("DD MMM, YYYY") : "-",
            },
            {
              key: "isActive", label: "Status", center: true, sortable: true,
              render: (_: any, row: ISeason) => row.isActive
                ? <Tag tone="ok">Active</Tag>
                : <Tag tone="neutral">Inactive</Tag>,
            },
            {
              key: "tournamentCount", label: "Tournaments", center: true,
              render: (_: any, row: ISeason) => row.tournamentCount || 0,
            },
            {
              key: "createdAt", label: "Created At", sortable: true,
              render: (_: any, row: ISeason) => moment(row.createdAt).format("DD MMM, YYYY"),
            },
          ]}
        />
      )}

      {isSeasonDialogOpen && (
        <SeasonDialog season={selectedSeason} isOpen={isSeasonDialogOpen} onClose={handleDialogClose} />
      )}

      {isDeleteDialogOpen && seasonToDelete && (
        <DeleteSeasonDialog
          seasonName={seasonToDelete.name}
          tournamentCount={seasonToDelete.tournamentCount}
          isOpen={isDeleteDialogOpen}
          onDelete={handleDeleteConfirm}
          onClose={(update) => {
            setIsDeleteDialogOpen(false);
            if (update) setSeasonToDelete(undefined);
          }}
        />
      )}
    </Card>
  );
};

export default SeasonsManagement;
